import { ReactNode, useCallback, useState } from 'react';
import { Check, LockKeyhole, Menu, Moon, ShieldCheck, Sun, X } from 'lucide-react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useExam } from '../context/ExamContext';
import { useTheme } from '../hooks/useTheme';
import { usePhonePairing } from '../context/PhonePairingContext';
import { Button } from './Button';
import { EndSessionDialog } from './EndSessionDialog';

interface HeaderLink { label: string; to: string; locked?: boolean; completed?: boolean }

function HeaderFrame({ links, actions, label }: { links: HeaderLink[]; actions?: ReactNode; label: string }) {
  const [open, setOpen] = useState(false);
  const { dark, toggle } = useTheme();
  return (
    <header className="sticky top-0 z-40 border-b border-white/10 bg-midnight/80 backdrop-blur-xl">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-3">
        <Link to="/" className="focus-ring flex items-center gap-3 rounded-md font-bold" aria-label="ExamSphere AI home">
          <span className="grid h-10 w-10 place-items-center rounded-full bg-gradient-to-br from-neon-violet to-neon-blue text-white shadow-neon"><ShieldCheck className="h-5 w-5" /></span>
          <span>ExamSphere AI</span>
        </Link>
        <nav aria-label={label} className="hidden items-center gap-1 md:flex">
          {links.map((link) => <HeaderLinkItem key={`${link.to}-${link.label}`} link={link} />)}
        </nav>
        <div className="flex items-center gap-2">
          <div className="hidden md:block">{actions}</div>
          <Button variant="ghost" icon={dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />} onClick={toggle} aria-label={dark ? 'Switch to light mode' : 'Switch to dark mode'} />
          <button type="button" className="focus-ring rounded-md p-2 md:hidden" onClick={() => setOpen((value) => !value)} aria-expanded={open} aria-controls="mobile-navigation" aria-label={open ? 'Close navigation menu' : 'Open navigation menu'}>
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>
      {open ? <nav id="mobile-navigation" aria-label={`${label} mobile`} className="border-t border-white/10 px-4 py-3 md:hidden"><div className="mx-auto grid max-w-7xl gap-1">{links.map((link) => <HeaderLinkItem key={`${link.to}-${link.label}`} link={link} onNavigate={() => setOpen(false)} />)}<div className="pt-2">{actions}</div></div></nav> : null}
    </header>
  );
}

function HeaderLinkItem({ link, onNavigate }: { link: HeaderLink; onNavigate?: () => void }) {
  if (link.locked) return <span aria-disabled="true" title="Complete the previous step first" className="flex cursor-not-allowed items-center gap-2 rounded-full px-3 py-2 text-sm text-slate-500"><LockKeyhole className="h-3.5 w-3.5" />{link.label}</span>;
  return <NavLink onClick={onNavigate} to={link.to} className={({ isActive }) => `focus-ring flex items-center gap-2 rounded-full px-3 py-2 text-sm font-medium transition ${isActive ? 'bg-white text-midnight' : 'text-slate-300 hover:bg-white/[0.08]'}`}>{link.completed ? <Check className="h-3.5 w-3.5 text-emerald-400" /> : null}{link.label}</NavLink>;
}

export function PublicHeader() {
  const links = [
    { label: 'Features', to: '/#features' }, { label: 'How It Works', to: '/#how-it-works' },
    { label: 'AI Proctoring', to: '/#ai-proctoring' }, { label: 'Dashboards', to: '/#dashboards' },
    { label: 'Reports', to: '/#reports' }, { label: 'Documentation', to: '/#documentation' }
  ];
  return <HeaderFrame label="Public navigation" links={links} actions={<div className="flex gap-2"><Link to="/login"><Button variant="ghost">Sign In</Button></Link><Link to="/login"><Button>Start Mock Exam</Button></Link></div>} />;
}

export function CandidateHeader() {
  const { state, logout } = useExam();
  const { endSession } = usePhonePairing();
  const navigate = useNavigate();
  const location = useLocation();
  const [confirmingExit, setConfirmingExit] = useState(false);
  const workflow = state.workflow;
  const links: HeaderLink[] = [
    { label: 'Instructions', to: '/instructions', completed: workflow.instructionsAcknowledged },
    { label: 'Permissions', to: '/permissions', completed: workflow.permissionsCompleted, locked: !workflow.instructionsAcknowledged },
    { label: 'Camera Setup', to: '/camera-setup', completed: workflow.cameraSetupCompleted, locked: !workflow.permissionsCompleted },
    { label: 'Exam', to: '/exam', locked: !workflow.cameraSetupCompleted },
    { label: 'Help', to: '/#documentation' }
  ];
  const exit = useCallback(() => {
    endSession();
    logout();
    navigate('/');
  }, [endSession, logout, navigate]);
  const canEndSession = !state.startedAt && !state.finishedAt && location.pathname !== '/phone-pairing';
  return (
    <>
      <HeaderFrame label="Candidate workflow" links={links} actions={canEndSession ? <Button variant="ghost" onClick={() => setConfirmingExit(true)}>End Session</Button> : undefined} />
      <EndSessionDialog open={confirmingExit} onClose={() => setConfirmingExit(false)} onConfirm={exit} />
    </>
  );
}

export function ReviewerHeader() {
  const { logout } = useExam(); const navigate = useNavigate();
  const exit = () => { logout(); navigate('/'); };
  return <HeaderFrame label="Reviewer navigation" links={[{ label: 'Sessions', to: '/proctor-dashboard' }, { label: 'Live Review', to: '/proctor-dashboard' }, { label: 'Incident Timeline', to: '/proctor-dashboard' }, { label: 'Candidate Reports', to: '/report' }, { label: 'Reviewer Notes', to: '/proctor-dashboard' }]} actions={<Button variant="ghost" onClick={exit}>Sign Out</Button>} />;
}

export function AdminHeader() {
  const { logout } = useExam(); const navigate = useNavigate();
  const exit = () => { logout(); navigate('/'); };
  return <HeaderFrame label="Administrator navigation" links={[{ label: 'Admin Dashboard', to: '/admin' }, { label: 'Exams', to: '/admin' }, { label: 'Candidates', to: '/admin' }, { label: 'Reviewers', to: '/admin' }, { label: 'Sessions', to: '/proctor-dashboard' }, { label: 'Reports', to: '/report' }, { label: 'System Settings', to: '/admin' }]} actions={<Button variant="ghost" onClick={exit}>Sign Out</Button>} />;
}
